
import base from './Base'

/**
 * cx.array
 * 类-数组语法糖
 *
 * @class
 * @memberof    cx
 * @author      Zhang Fayong
 * @since       2018-04-27
 */
class Array
{
  /**
   * 将数组转成map
   * @method
   * @static
   * @param {Array.Object}  data  带转换的数据
   * @param {String}        key   转换成map所依据的key
   * @returns {Object.Array}      转换成的map对象
   */
  static listToMap(data, key)
  {
    let map = {}
    data.forEach(obj =>
    {
      map[obj[key]] = map[obj[key]] || []
      map[obj[key]].push(obj)
    })
    return map
  }

  /**
   * 根据keyList中的多组key序列，把data转化为map对象。对象的键值是各key值字段对应值拼成的字符串，值是数组中对象包含该键值的对象序列
   * 应用场景：数据序列中多个字段是相同信息，并需要用该信息进行分组时
   * @method
   * @static
   * @param {Array.Object}                  data       待转换的数据
   * @param {Array.<Array.<String>>}        keysList   转换成map所依据的keys集合，如[[name,id],[no],[press,color]]
   * @returns {Object.Array}  转换成的map对象
   *
   * 如    var arr = [{begin: 1, end: 2},{begin:3, end:1},{begin:4, end: 2},{begin: 5, end:1}]
   *       listToMapByKeys(arr,['begin'],['end'])
   */
  static listToMapByKeys(data, keysList)
  {
    if (keysList.some(item => item.length === 0))
      return data

    let map = {}
    data.forEach(obj =>
    {
      let toMap = keys =>
      {
        let key = keys.map(key => obj[key]).join('_')
        map[key] = map[key] || []
        map[key].push(obj)
      }
      keysList.forEach(toMap)
    })
    if (data.every(item => item.id))
      Object.keys(map).forEach(key => cx.base.extend(map, {[key]: cx.util.filterRepeat(map[key])}))
    return map
  }

  /**
   * 判断数组是否为空数组
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @returns {Boolean}
   */
  static isEmpty(arr)
  {
    return arr.length === 0
  }

  /**
   * 判断数组是否包含指定元素
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {Object}    element             元素
   * @returns {Boolean}
   */
  static has(arr, element)
  {
    if (base.isArr(arr))
    {
      for (let i = arr.length - 1; i >= 0; i--)
      {
        if (arr[i] === element)
          return true
      }
    }
    return false
  }

  /**
   * 对数组元素进行循环操作
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {cx.Caller} callback        回调函数(function(obj))
   * @param   {Object}    context         上下文
   * @returns {Object}                    this
   */
  static forEach(arr, callback, context)
  {
    if (base.isArr(arr))
    {
      for (let i = 0; i < arr.length; i++)
      {
        if (callback.call(context, arr[i]))
          break
      }
    }
    return this
  }

  /**
   * 清空
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @returns {Object}                    this
   */
  static clear(arr)
  {
    if (base.isArr(arr))
    {
      for (let i = arr.length - 1; i >= 0; i--)
        arr.splice(i, 1)
    }
    return this
  }

  /**
   * 查找元素在数组中的下标
   * @static
   * @method
   * @param   {Array}         arr             数组
   * @param   {Object}        val             值
   */
  static indexOf(arr, val)
  {
    if (base.isArr(arr))
    {
      for (let i = arr.length - 1; i >= 0; i--)
      {
        if (arr[i] === val)
          return i
      }
    }
    return -1
  }

  /**
   * 取满足条件的第一个元素
   * @static
   * @method
   * @param   {Array}         arr             数组
   * @param   {Object}        exp             条件
   * @returns {Object}
   */
  static get1(arr, exp)
  {
    let ise
    let i, j
    if (base.isArr(arr))
    {
      for (i = 0; i < arr.length; i++)
      {
        ise = true
        for (j in exp)
        {
          if (arr[i][j] !== exp[j])
            ise = false
        }
        if (ise)
          return arr[i]
      }
    }
    return null
  }

  /**
   * 取满足条件的所有元素
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {Object}    exp                 条件
   * @returns {Array}
   */
  static gets(arr, exp)
  {
    let ret = []
    let ise
    let i, j
    if (base.isArr(arr))
    {
      for (i = 0; i < arr.length; i++)
      {
        ise = true
        for (j in exp)
        {
          if (arr[i][j] !== exp[j])
          {
            ise = false
            break
          }
        }
        if (ise)
          ret.push(arr[i])
      }
    }
    return ret
  }

  /**
   * 取不满足条件的所有元素
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {Object}    exp                 条件
   * @returns {Array}
   */
  static getsNot(arr, exp)
  {
    let ret = []
    let ise
    let i, j
    if (base.isArr(arr))
    {
      for (i = 0; i < arr.length; i++)
      {
        ise = true
        for (j in exp)
        {
          if (arr[i][j] === exp[j])
          {
            ise = false
            break
          }
        }
        if (ise)
          ret.push(arr[i])
      }
    }
    return ret
  }

  /**
   * 模糊查找取不满足条件的所有元素
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {String}    fld                 键名
   * @param   {Object}    val                 值
   * @returns {Array}
   */
  static getsNotFuz(arr, fld, val)
  {
    let ret = []
    if (base.isArr(arr))
    {
      for (let i = 0; i < arr.length; i++)
      {
        if (arr[i][fld].indexOf(val) < 0)
          ret.push(arr[i])
      }
    }
    return ret
  }

  /**
   * 从数组中模糊查找数据
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {String}    fld                 键名
   * @param   {Object}    val                 值
   * @returns {Array}
   */
  static getsFuz(arr, fld, val)
  {
    let ret = []
    if (base.isArr(arr))
    {
      for (let i = 0; i < arr.length; i++)
      {
        if (arr[i][fld].indexOf(val) > -1)
          ret.push(arr[i])
      }
    }
    return ret
  }

  /**
   * 把对象增加数组中，插入到开头位置
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    obj             待插入对象
   * @returns {Object}                    this
   */
  static insert(arr, obj)
  {
    if (base.isArr(arr))
      arr.unshift(obj)
    return this
  }

  /**
   * 把对象增加数组中，插入到指定位置
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    obj             待插入对象
   * @param   {Number}    idx             插入位置
   * @returns {Object}                    this
   */
  static insertAt(arr, obj, idx)
  {
    if (base.isArr(arr))
    {
      if (!base.isArr(obj))
        arr.splice(idx, 0, obj)
      else
      {
        for (let i = arr.length - 1; i >= 0; i--)
          arr.splice(idx, 0, obj[i])
      }
    }
    return this
  }

  /**
   * 添加多个元素到数组
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    elments         变参（支持多个元素）
   * @returns {Object}                    this
   */
  static append(arr, ...elments)
  {
    if (base.isArr(arr))
    {
      for (let elment of elments)
        arr.push(elment)
    }
    return this
  }

  /**
   * 添加数组中的元素到数组
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Array}     src             源数组
   * @returns {Object}                    this
   */
  static appendArr(arr, src)
  {
    if (base.isArr(arr) && base.isArr(src))
    {
      for (let i = 0; i < src.length; i++)
        arr.push(src[i])
    }
    return this
  }
  
  /**
   * 构造指定长度的数组
   * @method
   * @static
   * @param   {Number}   length      指定的数组长度
   * @param   {Number}   [defVal]    默认填充值，没有时就用对于序号填充
   * @returns {Array}    构造好的数组
   */
  static makeArr(length, defVal)
  {
    return Array.from({length}).map((item, index) => (cx.base.isDef(defVal) ? defVal : index))
  }

  /**
   * 从数组中删除指定对象的数据
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    exp             条件
   * @returns {Object}                    this
   */
  static remove(arr, exp)
  {
    let ise
    let i, j
    if (base.isArr(arr))
    {
      for (i = arr.length - 1; i >= 0; i--)
      {
        ise = true
        for (j in exp)
        {
          if (arr[i][j] !== exp[j])
          {
            ise = false
            break
          }
        }
        if (ise)
          arr.splice(i, 1)
      }
    }
    return this
  }

  /**
   * 从数组中删除指定位置删除多个元素
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Number}    idx             删除位置
   * @param   {Number}    number          个数
   * @returns {Object}                    this
   */
  static removeAt(arr, idx, number)
  {
    if (base.isArr(arr))
      arr.splice(idx, number)
    return this
  }

  /**
   * 从数组中删除指定元素
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    element         数组元素
   * @returns {Object}                    this
   */
  static removeElement(arr, element)
  {
    if (base.isArr(arr))
    {
      for (let i = arr.length - 1; i >= 0; i--)
      {
        if (arr[i] === element)
          arr.splice(i, 1)
      }
    }
    return this
  }

  /**
   * 把对象更新到数组中
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Object}    exp             条件
   * @param   {Object}    obj             对象
   * @returns {Object}                    this
   */
  static update(arr, exp, obj)
  {
    let ise
    let i, j
    if (base.isArr(arr))
    {
      for (i in arr)
      {
        ise = true
        for (j in exp)
        {
          if (arr[i][j] !== exp[j])
          {
            ise = false
            break
          }
        }
        if (ise)
        {
          arr[i] = obj
          break
        }
      }
    }
    return this
  }

  /**
   * 合并数组(把arr2合并到arr, 返回arr)
   * @static
   * @method
   * @param   {Array}     arr             数组
   * @param   {Array}     arr2            数组2
   * @param   {Boolean}   noMulti         是否剔除重复数据
   * @returns {Object}                    this
   */
  static merge(arr, arr2, noMulti)
  {
    if (base.isArr(arr) && base.isArr(arr2))
    {
      for (let i = 0; i < arr2.length; i++)
      {
        if (!noMulti || !this.has(arr2[i]))
          arr.push(arr2[i])
      }
    }
    return this
  }

  /**
   * 将数组分割为指定长度的多个子数组。
   * @method
   * @static
   * @param   {Array<Object>}   arr     待分割的数组
   * @param   {Number}          size    子数组的长度（最后一个子数组的长度 <= size）
   * @returns {Array<Array>}    构造好的多个子数组
   */
  static splitArray(arr, size = 999)
  {
    return Array.from({length: Math.floor(arr.length / size) + 1}).map((item, index) => arr.slice(index * size, (index + 1) * size)).filter(arr => arr.length !== 0)
  }

  /**
   * 把数组转换为字符串
   * @static
   * @method
   * @param   {Array}     arr                 数组
   * @param   {String}    [keyFld]            数组成员对象的keyFld属性的值添加到字符串序列
   * @param   {String}    [separator = ',']   转换为字符串的值分隔符，默认为逗号
   */
  static toString(arr, keyFld, separator)
  {
    let str = ''
    separator = separator || ','
    if (base.isArr(arr))
    {
      for (let item of arr)
      {
        if (base.isObj(item))
          str += (str ? separator : '') + item[keyFld]
        else
          str += (str ? separator : '') + item
      }
    }
    return str
  }
}

export default Array



// WEBPACK FOOTER //
// ./src/lib/Array.js